import * as React from 'react';
import { ComponentProps, forwardRef, RefObject, ReactNode, useMemo } from 'react';
import { IconButtonVariantType, IconComponentType } from '../icon-button';
import { Spinner } from '../spinner';
import { Heading, focusRing, focusRingReversed } from '@restworld/ui-ds-base';

import { cn } from '@restworld/utils-common';
export const ButtonV2 = forwardRef<HTMLButtonElement, ButtonV2Props>((props, ref) => {
  const {
    StartIcon,
    EndIcon,
    children,
    disabled,
    fullwidth,
    twStyle,
    indicator,
    loading,
    onClick,
    testId,
    size = 'md',
    variant = 'primary',
    ...restProps
  } = props;
  const iconSizeClasses = '!h-5 !w-5';
  const iconColorClasses = useMemo(() => iconColors[variant], [variant]);
  const iconClasses = useMemo(
    () => `${iconSizeClasses} ${disabled ? 'text-neutral-100' : iconColorClasses}`,
    [disabled, iconColorClasses]
  );
  const isTextOnlyButtonLoading = useMemo(() => !StartIcon && !EndIcon && loading, [EndIcon, StartIcon, loading]);

  const startIconEl = useMemo(
    () =>
      StartIcon && loading ? (
        <StyledSpinner className={iconSizeClasses} color={buttonToSpinnerVariantMap[variant]} size={size} />
      ) : StartIcon ? (
        <StartIcon className={iconClasses} />
      ) : null,
    [StartIcon, iconClasses, loading, size, variant]
  );
  const endIconEl = useMemo(
    () =>
      EndIcon && loading ? (
        <StyledSpinner className={iconSizeClasses} color={buttonToSpinnerVariantMap[variant]} size={size} />
      ) : EndIcon ? (
        <EndIcon className={iconClasses} />
      ) : null,
    [EndIcon, iconClasses, loading, size, variant]
  );

  return (
    <StyledButton
      ref={ref}
      size={size}
      variant={variant}
      onClick={(e) => {
        // e.preventDefault();
        onClick?.(e);
      }}
      disabled={disabled}
      fullwidth={fullwidth}
      data-cy={testId}
      twStyle={twStyle}
      {...restProps}
    >
      {startIconEl}
      <ContentWrapper isTextOnlyButtonLoading={isTextOnlyButtonLoading}>{children}</ContentWrapper>
      {endIconEl}
      {isTextOnlyButtonLoading && (
        <LoadingOverlay>
          <StyledSpinner size={size} className={iconSizeClasses} color={buttonToSpinnerVariantMap[variant]} />
        </LoadingOverlay>
      )}
      <StyledOverlay disabled={disabled} variant={variant} />
      {!!indicator && (
        <StyledNotificationIndicator>
          {typeof indicator === 'number' && (
            <Heading as="span" size={6} weight="semibold" className="text-neutral-10 !text-sm">
              {indicator}
            </Heading>
          )}
        </StyledNotificationIndicator>
      )}
    </StyledButton>
  );
});
ButtonV2.displayName = 'ButtonV2';

export default ButtonV2;

const iconColors: Record<ButtonVariantType, string> = {
  primary: 'text-purple-700',
  'secondary-neutral': 'text-neutral-700',
  'secondary-success': 'text-green-700',
  'secondary-warning': 'text-orange-700',
  'secondary-danger': 'text-red-700',
  'secondary-restworld': 'text-purple-700',
  'secondary-reversed': 'text-neutral-10',
  tertiary: 'text-neutral-700',
  'tertiary-reversed': 'text-neutral-10',
};
const buttonToSpinnerVariantMap: Record<ButtonVariantType, IconButtonVariantType> = {
  primary: 'restworld',
  'secondary-neutral': 'neutral',
  'secondary-success': 'success',
  'secondary-warning': 'warning',
  'secondary-danger': 'error',
  'secondary-restworld': 'restworld',
  'secondary-reversed': 'reversed',
  tertiary: 'neutral',
  'tertiary-reversed': 'reversed',
};
const variantToOverlayBgMap: Record<ButtonVariantType, string> = {
  primary: 'bg-neutral-700',
  'secondary-neutral': 'bg-neutral-900',
  'secondary-success': 'bg-green-700',
  'secondary-warning': 'bg-orange-700',
  'secondary-danger': 'bg-red-700',
  'secondary-restworld': 'bg-purple-500',
  'secondary-reversed': 'bg-neutral-10',
  tertiary: 'bg-neutral-700',
  'tertiary-reversed': 'bg-white',
};

export interface ButtonV2Props extends ComponentProps<'button'> {
  children?: ReactNode;
  ref?: ((instance: HTMLButtonElement | null) => void) | RefObject<HTMLButtonElement | null> | null;
  disabled?: boolean;
  StartIcon?: IconComponentType;
  EndIcon?: IconComponentType;
  twStyle?: string;
  size?: ButtonSizeType;
  variant?: ButtonVariantType;
  fullwidth?: boolean;
  indicator?: boolean | number;
  loading?: boolean;
  testId?: string;
}
type ButtonSizeType = 'sm' | 'md';
export type ButtonVariantType =
  | 'primary'
  | 'secondary-neutral'
  | 'secondary-success'
  | 'secondary-warning'
  | 'secondary-danger'
  | 'secondary-restworld'
  | 'secondary-reversed'
  | 'tertiary'
  | 'tertiary-reversed';

const ButtonVariantStyles: Record<ButtonVariantType, string> = {
  primary: 'text-purple-700 bg-fluo-500',
  'secondary-neutral': 'text-neutral-700 bg-neutral-900/10',
  'secondary-restworld': 'text-purple-700 bg-purple-500/10',
  'secondary-success': 'text-green-700 bg-green-700/10',
  'secondary-warning': 'text-orange-700 bg-orange-700/10',
  'secondary-danger': 'text-red-700 bg-red-700/10',
  'secondary-reversed': 'text-neutral-10 bg-neutral-10/10',
  tertiary: `text-neutral-700 bg-transparent shadow-[inset_0_0_0_1px_theme('colors.neutral.90')]`,
  'tertiary-reversed': `text-neutral-10 bg-transparent shadow-[inset_0_0_0_1px_theme('colors.neutral.90')]`,
};

const StyledButton = ({ size, variant, fullwidth, disabled, loading, twStyle, className, ...props }: any) => (
  <button
    className={cn(
      // Shared WCAG focus ring (purple-500); reversed variants sit on dark surfaces → white.
      `relative flex gap-2 items-center justify-center font-semibold transition duration-200 ease-in-out rounded-lg !py-2 ${focusRing} disabled:bg-neutral-40`,
      variant?.endsWith('reversed') && focusRingReversed,
      size === 'sm' ? '!text-sm !px-3 !h-8' : '!text-base !h-12 !px-4',
      [ButtonVariantStyles[variant], 'disabled:text-neutral-200 disabled:cursor-not-allowed'],
      fullwidth ? 'w-full' : '',
      disabled || loading ? 'cursor-not-allowed pointer-events-none' : 'cursor-pointer pointer-events-auto',
      twStyle,
      className
    )}
    {...props}
  />
);
const StyledOverlay = ({ variant, disabled, className, ...props }: any) => (
  <span
    className={cn(
      'absolute top-0 left-0 right-0 bottom-0 opacity-0 hover:opacity-15 active:opacity-20 transition duration-200 ease-in-out rounded-lg',
      variantToOverlayBgMap[variant],
      disabled ? 'hover:opacity-0 active:opacity-0' : '',
      className
    )}
    {...props}
  />
);
const StyledNotificationIndicator = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span
    className={cn('h-6 w-6 rounded-full bg-red-500 absolute -top-2 -right-2 grid place-items-center', className)}
    {...props}
  />
);
const LoadingOverlay = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span
    className={cn('absolute top-0 left-0 right-0 bottom-0 bg-transparent grid place-items-center', className)}
    {...props}
  />
);
const ContentWrapper = ({ isTextOnlyButtonLoading, className, ...props }: any) => (
  <span className={cn(isTextOnlyButtonLoading ? 'opacity-0' : '', className)} {...props} />
);
const StyledSpinner = ({ size, className, ...props }: any) => (
  <Spinner className={cn(size === 'md' ? 'relative -top-[1.5px]' : 'relative -top-[0.5px]', className)} {...props} />
);
